export type SyncOperation = "created" | "updated" | "cancelled";

export type SyncRow = {
  orderId: number;
  vehicle: string | null;
  plate: string | null;
  driver: string | null;
  action: string;
  reason: string;
  before: string | null;
  after: string | null;
  oldDate: string;
  newDate: string;
  warning: boolean;
  missingRequirements?: string[];
  restore?: { token: string; expiresAt: number };
};

export type SyncReport = {
  bobReference?: number;
  operation?: SyncOperation;
  existing: boolean;
  changed: boolean;
  warnings: string[];
  open: number;
  rows: SyncRow[];
};

export type TransportSyncAnswer = { data?: { sync?: SyncReport } };
export type RestoredAssignment = { row: SyncRow; open: number };

// Older Transplan versions can still return these technical notices during a staggered deployment.
const routingNotices = [
  "Fahrzeitabfrage für die Fahrbarkeitsprüfung fehlgeschlagen; bestehende Zuordnung bleibt.",
  "Fahrbarkeit nicht vollständig prüfbar; bestehende Zuordnung bleibt.",
  "Anfahrt nicht sicher berechenbar; Disposition prüfen.",
  "Andere Fahrt desselben LKW nicht vollständig prüfbar; Disposition prüfen.",
];
const retainedReason = "LKW erfüllt den aktuellen Fahrzeugbedarf; Zuordnung beibehalten.";
function cleanNotice(text: string): string {
  const cleaned = routingNotices.reduce((value, notice) => value.split(notice).join(""), text).replace(/\s+/g, " ").trim();
  return cleaned !== text && /^[^:]+:$/.test(cleaned) ? "" : cleaned;
}
const dateLabel = (date: string) => date.replace(/^(\d{4})-(\d{2})-(\d{2})$/, "$3.$2.$1");

export function syncOperation(report: SyncReport | null, status?: string): SyncOperation {
  if (status === "DELETED" || report?.operation === "cancelled" || report?.rows.some((row) => row.action === "cancelled")) return "cancelled";
  return report?.operation ?? (report && !report.existing ? "created" : "updated");
}

export function syncSummary(report: SyncReport | null, operation: SyncOperation) {
  if (report?.existing && !report.changed) {
    return operation === "cancelled"
      ? { title: "Bereits storniert", message: "Der Auftrag ist in Transplan bereits storniert.", severity: "info" as const }
      : { title: "Keine Änderungen in Transplan", message: "Der Auftrag ist bereits aktuell. Es waren keine Änderungen nötig.", severity: "info" as const };
  }
  if (operation === "cancelled") return { title: "Stornierung übertragen", message: "Die Stornierung wurde an Transplan übertragen.", severity: "success" as const };
  if (operation === "updated" && report) return { title: "Änderung übertragen", message: "Die Änderung wurde an Transplan übertragen.", severity: "success" as const };
  return { title: "Auftrag übertragen", message: "Der Auftrag wurde an Transplan übertragen.", severity: "success" as const };
}

export function syncVehicleLabel(row: SyncRow): string {
  return [row.vehicle?.trim() || "LKW", row.plate?.trim(), row.driver?.trim()].filter(Boolean).join(" · ");
}

/** One status line, with dates/clocks only when they explain a real change. */
export function syncRowMessage(row: SyncRow): string {
  const reason = cleanNotice(row.reason);
  const dateChanged = row.oldDate && row.newDate && row.oldDate !== row.newDate;
  if (dateChanged && row.action === "day_preserved") {
    return `Auftragsdatum geändert: ${dateLabel(row.oldDate)} → ${dateLabel(row.newDate)}. Tagesauftrag und LKW-Zuordnung bleiben am bisherigen Tag erhalten; am neuen Tag neu disponieren.`;
  }
  if (dateChanged && row.action === "released") {
    return `Auftragsdatum geändert: ${dateLabel(row.oldDate)} → ${dateLabel(row.newDate)}. LKW-Zuordnung aufgehoben; am neuen Tag neu disponieren.`;
  }
  if (row.action === "released") {
    if (row.missingRequirements?.length) return `LKW-Zuordnung aufgehoben; folgende Anforderungen fehlen: ${row.missingRequirements.join(", ")}.`;
    return reason.includes("LKW-Zuordnung aufgehoben") ? reason : `LKW-Zuordnung aufgehoben${reason ? `: ${reason}` : "."}`;
  }
  if (row.action === "cancelled") return reason || "Auftrag storniert; Tagesstand bleibt erhalten.";
  if (row.action === "restored") return reason || "LKW-Zuordnung wiederhergestellt; Ausnahme gespeichert.";

  const changes = [];
  if (row.action === "slot_changed") changes.push("Fahrzeugbedarf neu zugeordnet.");
  const before = row.before?.trim(), after = row.after?.trim();
  if (before !== after && (before || after)) {
    changes.push(before && after ? `Uhrzeit geändert: ${before} → ${after}.` : after ? `Uhrzeit festgelegt: ${after}.` : "Konkrete Uhrzeit entfällt.");
  }
  changes.push(changes.length && reason.startsWith(retainedReason)
    ? `LKW-Zuordnung beibehalten.${reason.slice(retainedReason.length)}`
    : reason || "LKW-Zuordnung beibehalten.");
  return changes.join(" ");
}

export function syncRowHasWarning(row: SyncRow): boolean {
  const onlyRoutingNotice = routingNotices.some((notice) => row.reason.includes(notice))
    && ["", retainedReason, "Unveränderte Übernahme; bestehende Zuordnung beibehalten."].includes(cleanNotice(row.reason));
  return row.warning && !onlyRoutingNotice;
}

/** Vehicle-specific warnings already appear in that vehicle's status line. */
export function syncWarnings(report: SyncReport): string[] {
  return [...new Set(report.warnings)].filter((warning) => {
    const text = cleanNotice(warning);
    if (!text) return false;
    return !report.rows.some((row) => {
      const prefix = `${row.vehicle}: `;
      const detail = cleanNotice(warning.startsWith(prefix) ? warning.slice(prefix.length) : warning);
      return !detail || cleanNotice(row.reason).includes(detail);
    });
  }).map(cleanNotice);
}
